import React from "react";

/**
 * SiteErrorBoundary
 * -----------------
 * Wraps the whole <Routes> tree so a single-page crash doesn't blank the site.
 * If ANY page throws (missing import, undefined function, network parse error,
 * etc.) we render a friendly "Something hiccupped" panel with a link back to
 * the home page — instead of the entire <App> unmounting to a navy void.
 *
 * Built specifically because of the May 2026 RequestPacket-missing-import
 * incident that took moovinforward.org dark for an entire afternoon.
 */
class SiteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface the crash to console so we can read it in browser dev tools.
    // Also fire-and-forget log to the backend so Shannon's audit trail captures it.
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      if (BACKEND_URL) {
        fetch(`${BACKEND_URL}/api/public/client-crash`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: String(error?.message || error || "unknown"),
            stack: String(error?.stack || "").slice(0, 4000),
            component_stack: String(info?.componentStack || "").slice(0, 2000),
            url: typeof window !== "undefined" ? window.location.href : "",
            user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
          }),
        }).catch(() => {});
      }
    } catch (e) { /* swallow */ }
    console.error("SiteErrorBoundary caught:", error, info);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }
    const msg = String(this.state.error?.message || this.state.error || "Something went sideways.");
    return (
      <div data-testid="site-error-boundary" style={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #0a1428 0%, #1b2a4e 100%)",
        color: "#F3F4F6",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem 1.2rem",
        fontFamily: "Helvetica, Arial, sans-serif",
      }}>
        <div style={{
          maxWidth: 560,
          width: "100%",
          background: "rgba(15, 23, 42, 0.85)",
          border: "1px solid rgba(184, 145, 90, 0.5)",
          borderLeft: "4px solid #B8915A",
          borderRadius: 10,
          padding: "1.6rem 1.8rem",
          boxShadow: "0 28px 60px rgba(0,0,0,0.5)",
        }}>
          <div style={{
            fontSize: "0.68rem",
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: "#B8915A",
            fontWeight: 700,
            marginBottom: 6,
          }}>
            Moovin Forward
          </div>
          <h1 style={{ fontSize: "1.4rem", margin: "0 0 0.6rem", color: "#F3F4F6" }}>
            One page hiccupped — the rest of the site is still up.
          </h1>
          <p style={{ color: "#9CA3AF", fontSize: "0.92rem", lineHeight: 1.55, margin: "0 0 1rem" }}>
            We caught the error before it took the whole site down. You can head back
            home and try again. The crew has already been notified.
          </p>
          <div style={{
            background: "rgba(220, 38, 38, 0.08)",
            border: "1px solid rgba(220, 38, 38, 0.3)",
            borderRadius: 6,
            padding: "0.65rem 0.85rem",
            fontSize: "0.78rem",
            color: "#FCA5A5",
            fontFamily: "monospace",
            marginBottom: "1.1rem",
            wordBreak: "break-word",
          }}>
            {msg}
          </div>
          <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
            <a href="/" data-testid="site-error-go-home" style={{
              background: "#B8915A",
              color: "#1b2a4e",
              border: "1px solid #8a6c3e",
              padding: "0.65rem 1.4rem",
              borderRadius: 5,
              fontWeight: 700,
              fontSize: "0.9rem",
              textDecoration: "none",
              display: "inline-block",
            }}>
              ← Back to Home
            </a>
            <button type="button" onClick={this.reset} data-testid="site-error-retry" style={{
              background: "transparent",
              color: "#D1D5DB",
              border: "1px solid #4B5563",
              padding: "0.65rem 1.2rem",
              borderRadius: 5,
              fontWeight: 600,
              fontSize: "0.9rem",
              cursor: "pointer",
            }}>
              Try again
            </button>
          </div>
          <div style={{
            marginTop: "1.4rem",
            paddingTop: "1rem",
            borderTop: "1px solid rgba(255,255,255,0.06)",
            color: "#6B7280",
            fontSize: "0.74rem",
          }}>
            If this keeps happening, call Shannon directly at{" "}
            <a href="tel:+18173818831" style={{ color: "#B8915A" }}>(817) 381-8831</a>.
          </div>
        </div>
      </div>
    );
  }
}

export default SiteErrorBoundary;
